'use client'

import * as React from 'react'

import { cn } from '../cn'

export interface FilterChipGroupOption {
  value: string
  label: string
}

export interface FilterChipGroupProps {
  /** Section label (e.g. "Priority"). Shows a `(n)` count when the selection is non-empty. */
  label: string
  options: FilterChipGroupOption[]
  /** Currently selected values. Accepts a Set or array — both are common in caller state. */
  selected: Set<string> | readonly string[]
  onChange: (next: Set<string>) => void
  /** Optional extra classes on the outer wrapper. */
  className?: string
}

/**
 * Toggle-chip filter group used inside filter popovers (priority, provider,
 * source, status, etc.). Lives in `@ordo/ui` so every app's filter UI shares
 * the same chip styling — adopted from Tasker's FilterToolbar pattern.
 */
export function FilterChipGroup({ label, options, selected, onChange, className }: FilterChipGroupProps) {
  const selectedSet = React.useMemo<Set<string>>(
    () => (selected instanceof Set ? selected : new Set(selected)),
    [selected],
  )
  if (options.length === 0) {
    return null
  }
  const toggle = (value: string) => {
    const next = new Set(selectedSet)
    if (next.has(value)) {
      next.delete(value)
    } else {
      next.add(value)
    }
    onChange(next)
  }
  return (
    <div className={className}>
      <label className="mb-1.5 block text-sm text-muted-foreground md:mb-1 md:text-xs">
        {label}
        {selectedSet.size > 0 && <span className="ml-1 text-primary">({selectedSet.size})</span>}
      </label>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const active = selectedSet.has(opt.value)
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggle(opt.value)}
              className={cn(
                'rounded-md border px-3 py-2 text-sm transition-colors md:px-2 md:py-1 md:text-xs',
                active
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:border-foreground/30',
              )}>
              {opt.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
